export async function clientUploadToCloudflare(uploadURL: string, file: File): Promise<string | undefined> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(uploadURL, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    // Create detailed error for admins and user-friendly error for users
    const errorDetails = {
      status: response.status,
      statusText: response.statusText,
      url: uploadURL
    };
    
    console.error('Upload failed:', errorDetails);
    
    // Throw specific errors based on status code
    if (response.status === 429) {
      throw new Error('RATE_LIMIT_EXCEEDED');
    } else if (response.status >= 500) {
      throw new Error('SERVER_ERROR');
    } else if (response.status === 413) {
      throw new Error('FILE_TOO_LARGE');
    } else {
      throw new Error('UPLOAD_FAILED');
    }
  }

  const responseData = await response.json();

  const originalImageVariant = responseData.result.variants.find((v: any) => v.includes('orig2'));

  return originalImageVariant;
}

const getUploadUrl = async () => {
  const response = await fetch('/api/image/getUploadUrl');

  if (!response.ok) {
    const errorDetails = {
      status: response.status,
      statusText: response.statusText
    };
    console.error('Failed to get upload URL:', errorDetails);
    throw new Error('UPLOAD_URL_FAILED');
  }

  const responseData = await response.json();

  return responseData.uploadUrl;
};

export async function secureImageUpload(file: File): Promise<string | undefined> {
  let retries = 0;
  const maxRetries = 2;
  let lastError: Error | null = null;

  while (retries <= maxRetries) {
    try {
      const uploadUrl = await getUploadUrl();

      // Dispatch a custom event when uploading starts
      window.dispatchEvent(new CustomEvent('uploadStarted', { detail: file.name }));

      const image = await clientUploadToCloudflare(uploadUrl, file);

      if (image) {
        return image; // Success!
      } else {
        throw new Error('UPLOAD_RESPONSE_INVALID');
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('UNKNOWN_ERROR');
      console.error(`Upload attempt ${retries + 1} failed:`, {
        error: lastError.message,
        fileName: file.name,
        fileSize: file.size,
        attempt: retries + 1
      });
      
      // Don't retry on certain errors
      if (lastError.message === 'RATE_LIMIT_EXCEEDED' || 
          lastError.message === 'FILE_TOO_LARGE') {
        break;
      }
    }

    retries++;
  }

  // Throw the last error with context
  const finalError = lastError || new Error('UPLOAD_FAILED');
  finalError.message = `${finalError.message}|attempts:${retries}|fileSize:${file.size}`;
  throw finalError;
}
